import React from "react";
import { NavLink } from "react-router-dom";
import { FaQuoteLeft } from "react-icons/fa";
import { ArrowRight, Sparkles, GraduationCap, ShieldCheck, Award, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import logo from "../images/logo.png";
import logoNoText from "../images/logo-notext.png";
import AboutText from "./AboutText";
import Human from "./Human";

const About = () => {
  const { t } = useLanguage();

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-white min-h-screen relative overflow-hidden">
      {/* Decorative blur elements for visual warmth */}
      <div className="absolute -top-10 -right-10 w-80 h-80 glow-blob-secondary rounded-full filter blur-3xl opacity-30 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center space-x-2 bg-amber-50 text-secondary border border-amber-200 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-3">
            <Sparkles className="w-4 h-4 text-secondary" />
            <span>{t.about.badge}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary tracking-tight">
            {t.about.title}
          </h1>
          <p className="mt-3 text-base sm:text-lg text-gray-600 font-light leading-relaxed">
            {t.about.subtitle}
          </p>
        </div>

        {/* Bio & Coach Card */}
        <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-12 mb-14 sm:mb-18">
          <AboutText />
          
          <div className="lg:w-1/2 flex flex-col justify-between p-6 sm:p-8 bg-gradient-to-br from-amber-50/60 via-white to-blue-50/30 rounded-3xl border border-amber-100 shadow-lg relative overflow-hidden">
            <FaQuoteLeft className="text-secondary/15 text-5xl sm:text-6xl absolute top-6 right-6 pointer-events-none" />
            
            <div className="space-y-6 relative z-10">
              {/* Coach Profile Header */}
              <div className="flex items-center space-x-4">
                <div className="relative flex-shrink-0">
                  <img
                    src={`${process.env.PUBLIC_URL || ""}/images/charly.png?v=3`}
                    alt="Charles Malec - Coach & Career Mentor"
                    className="w-20 sm:w-24 h-auto rounded-2xl shadow-md border border-slate-200/80"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = logoNoText;
                    }}
                  />
                  <span className="absolute -bottom-1 -right-1 bg-emerald-500 w-4 h-4 rounded-full border-2 border-white shadow-sm" title="Disponible pour les sessions" />
                </div>

                <div>
                  <h3 className="font-extrabold text-xl sm:text-2xl text-primary">Charles Malec</h3>
                  <p className="text-xs sm:text-sm font-semibold text-secondary mb-2">{t.about.coachRole}</p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center space-x-1 bg-amber-100/90 text-amber-950 border border-amber-300/80 text-[11px] font-semibold px-2.5 py-0.5 rounded-full shadow-2xs">
                      <GraduationCap className="w-3.5 h-3.5 text-secondary" />
                      <span>{t.about.certificationPill}</span>
                    </span>
                    <span className="inline-block bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
                      {t.about.experiencePill}
                    </span>
                  </div>
                </div>
              </div>

              {/* Personal Quote */}
              <p className="text-gray-700 italic leading-relaxed text-base sm:text-lg">
                {t.about.personalQuote}
              </p>

              <div className="p-4 bg-white/90 rounded-2xl border border-blue-100 shadow-2xs text-xs sm:text-sm text-gray-600">
                <p className="font-semibold text-primary mb-1">{t.about.guaranteeTitle}</p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {t.about.guaranteeText}
                </p>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400 font-mono">The Charly Method</span>
              <img
                src={logo}
                alt="The Charly Method Logo"
                className="h-8 sm:h-10 w-auto opacity-80"
              />
            </div>
          </div>
        </div>

        {/* ACADEMIC CREDENTIAL & CERTIFICATION SECTION */}
        <div className="mb-14 sm:mb-20 bg-gradient-to-br from-amber-50/50 via-white to-slate-50/60 rounded-3xl p-6 sm:p-10 border border-amber-200/80 shadow-md">
          <div className="max-w-4xl mx-auto">
            {/* Header with University Seal */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pb-6 border-b border-amber-100">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-amber-100/80 border border-amber-300/80 flex items-center justify-center flex-shrink-0 text-secondary shadow-xs">
                <GraduationCap className="w-7 h-7 sm:w-8 sm:h-8" />
              </div>
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-wider text-secondary bg-amber-100/70 border border-amber-200 px-3 py-0.5 rounded-full mb-1">
                  {t.about.academicBadge}
                </span>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-primary">
                  {t.about.academicTitle}
                </h2>
                <p className="text-sm sm:text-base font-semibold text-gray-700 mt-0.5">
                  {t.about.academicInstitution}
                </p>
              </div>
            </div>

            {/* Explanation & Pillars */}
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed my-6">
              {t.about.academicSummary}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Pillar 1 */}
              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-primary flex items-center justify-center mb-3">
                    <ShieldCheck className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="text-sm font-bold text-primary mb-1.5">
                    {t.about.academicPillar1Title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t.about.academicPillar1Desc}
                  </p>
                </div>
              </div>

              {/* Pillar 2 */}
              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-secondary flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="text-sm font-bold text-primary mb-1.5">
                    {t.about.academicPillar2Title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t.about.academicPillar2Desc}
                  </p>
                </div>
              </div>

              {/* Pillar 3 */}
              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 text-primary flex items-center justify-center mb-3">
                    <Award className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="text-sm font-bold text-primary mb-1.5">
                    {t.about.academicPillar3Title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t.about.academicPillar3Desc}
                  </p>
                </div>
              </div>
            </div>

            {/* Proof Note */}
            <div className="mt-6 pt-4 border-t border-amber-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-gray-500">
              <span>{t.about.academicProofNote}</span>
              <NavLink
                to="/contact"
                className="font-semibold text-secondary hover:text-primary transition inline-flex items-center space-x-1"
              >
                <span>Échanger avec Charles →</span>
              </NavLink>
            </div>
          </div>
        </div>

        {/* HUMAN framework section */}
        <Human />

        {/* CTA below HUMAN section */}
        <div className="text-center mt-12 sm:mt-16 pt-8 border-t border-gray-100">
          <NavLink
            to="/contact"
            className="inline-flex items-center justify-center space-x-2 bg-primary text-white px-8 py-3.5 rounded-full text-base sm:text-lg font-semibold shadow-lg hover:bg-indigo-700 hover:shadow-xl transition transform hover:-translate-y-0.5 min-h-[48px] cursor-pointer"
          >
            <span>{t.about.ctaButton}</span>
            <ArrowRight className="w-5 h-5" />
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default About;
